import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { BRAND_NAME, CONTACT_EMAIL, CONTACT_PHONE, MAILING_ADDRESS } from '../config/brand';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Privacy Policy - ${BRAND_NAME}`,
    "url": "https://lajdigital.com/privacy"
  };

  return (
    <section className="pt-32 pb-24 relative bg-gray-950 min-h-screen">
      <Helmet>
        <title>Privacy Policy | {BRAND_NAME}</title>
        <meta name="description" content="Read the Privacy Policy of LAJ Digital to understand how we collect, use, and protect your data and personal information." />
        <link rel="canonical" href="https://lajdigital.com/privacy" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="container-custom max-w-4xl relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel p-8 md:p-12 rounded-3xl text-gray-300 font-light space-y-8"
        >
          <div className="border-b border-gray-800 pb-8 mb-8">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-xl font-display font-bold text-white">1. Introduction</h2>
            <p>
              At {BRAND_NAME} ("we", "our", or "us"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our services.
            </p>

            <h2 className="text-xl font-display font-bold text-white">2. Information We Collect</h2>
            <p>
              We may collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and services, participate in activities on the website, or contact us. The personal information that we collect depends on the context of your interactions with us and may include names, phone numbers, email addresses, company details, and a mobile number if you choose to opt in to text messaging.
            </p>

            <h2 className="text-xl font-display font-bold text-cyan-400 p-4 bg-cyan-900/10 border-l-2 border-cyan-500 rounded-r-xl">
              3. SMS and Text Messaging Policy
            </h2>
            <div className="p-4 bg-gray-900/50 rounded-xl space-y-4 border border-gray-800">
              <p className="font-medium text-white">
                We value your privacy and treat your mobile contact information as strictly confidential.
              </p>
              <p>
                <strong className="text-white">Consent is optional.</strong> Opting in to receive text messages from {BRAND_NAME} is never a condition of purchasing any product or service, creating an account, submitting a form, or otherwise doing business with us. You may decline SMS messaging and still use all of our services.
              </p>
              <p>
                <strong className="text-white">We do not share your number.</strong> Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent are never shared with any third party for any purpose. If you opt in, your phone number will be used only for the purposes you agreed to, such as responses to your inquiries, appointment reminders, customer support, and service updates, and will never be sold, rented, or distributed to outside marketing agencies.
              </p>
              <p>
                <strong className="text-white">Frequency, rates, and opt-out.</strong> Message frequency varies based on your interaction with our services. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages. You may reply STOP at any time to cancel, or HELP for assistance.
              </p>
              <p>
                <strong className="text-white">Service providers.</strong> We use a third-party messaging platform to deliver text messages on our behalf. That provider processes your number solely to transmit messages you have consented to receive and is not permitted to use it for any other purpose.
              </p>
            </div>

            <h2 className="text-xl font-display font-bold text-white">4. How We Use Your Information</h2>
            <p>
              We use personal information collected via our website for a variety of business purposes, including:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide, operate, and maintain our website and services.</li>
              <li>To improve, personalize, and expand our website.</li>
              <li>To understand and analyze how you use our website.</li>
              <li>To communicate with you directly for customer service, updates, and other service-related information.</li>
            </ul>

            <h2 className="text-xl font-display font-bold text-white">5. Data Security</h2>
            <p>
              We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
            </p>

            <h2 className="text-xl font-display font-bold text-white">6. Contact Us</h2>
            <p>
              If you have questions or comments about this notice, you can reach us at:
            </p>
            <ul className="list-none space-y-2 text-gray-200">
              <li><strong className="text-white">Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-300 underline hover:text-cyan-200">{CONTACT_EMAIL}</a></li>
              {CONTACT_PHONE && (
                <li><strong className="text-white">Phone:</strong> <a href={`tel:${CONTACT_PHONE.replace(/[^\d+]/g, '')}`} className="text-cyan-300 underline hover:text-cyan-200">{CONTACT_PHONE}</a></li>
              )}
              {MAILING_ADDRESS && (
                <li><strong className="text-white">Mailing Address:</strong> {MAILING_ADDRESS}</li>
              )}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;